import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { Addon, Cart } from '../../types';

interface AddonCardProps {
  addon: Addon;
  cart: Cart;
  onAddToCart: (addon: Addon, quantity: number) => void;
}

const AddonCard: React.FC<AddonCardProps> = ({ addon, cart, onAddToCart }) => {
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);

  const handleIncrement = () => {
    if (addon.stock && quantity < addon.stock) {
      setQuantity(quantity + 1);
    }
  };

  const handleDecrement = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleAdd = async () => {
    setAdding(true);
    try {
      await onAddToCart(addon, quantity);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className={`addon-card ${!addon.in_stock ? 'sold-out' : ''}`}>
      <div className="addon-card-content">
        <div className="addon-image-wrapper">
          {addon.image_url ? (
            <img
              src={addon.image_url}
              alt={addon.name}
              className="addon-image"
              loading="lazy"
            />
          ) : (
            <div className="addon-image-placeholder">
              <i className="fas fa-box-open"></i>
            </div>
          )}
          {!addon.in_stock && (
            <div className="sold-out-badge">
              <i className="fas fa-ban"></i>
              <span>Sold Out</span>
            </div>
          )}
        </div>
        
        <div className="addon-info">
          <div className="addon-header">
            {addon.has_variants ? (
              <Link
                to={addon.get_add_to_cart_url}
                className="addon-name-link"
                title={addon.name}
              >
                <h6 className="addon-name">{addon.name}</h6>
              </Link>
            ) : (
              <h6 className="addon-name" title={addon.name}>{addon.name}</h6>
            )}
            <div className="addon-price">
              {addon.discount_price ? (
                <>
                  <span className="addon-price-original">
                    {cart.currency?.symbol || '$'}{addon.price.toFixed(2)}
                  </span>
                  <span className="addon-price-current">
                    {cart.currency?.symbol || '$'}{addon.discount_price.toFixed(2)}
                  </span>
                </>
              ) : (
                <span className="addon-price-current">
                  {addon.price_display}
                </span>
              )}
            </div>
          </div>
          
          <div className="addon-actions">
            {!addon.has_variants && (
              <div className="addon-quantity-controls">
                <button
                  type="button"
                  className="quantity-btn quantity-btn-minus"
                  onClick={handleDecrement}
                  disabled={quantity <= 1 || !addon.in_stock}
                  aria-label="Decrease quantity"
                >
                  <i className="fas fa-minus"></i>
                </button>
                <span className="quantity-value">{quantity}</span>
                <button
                  type="button"
                  className="quantity-btn quantity-btn-plus"
                  onClick={handleIncrement}
                  disabled={addon.stock ? quantity >= addon.stock : false || !addon.in_stock}
                  aria-label="Increase quantity"
                >
                  <i className="fas fa-plus"></i>
                </button>
              </div>
            )}
            
            {addon.has_variants ? (
              <Link
                to={addon.get_add_to_cart_url}
                className={`btn-addon-action ${!addon.in_stock ? 'disabled' : ''}`}
                title="View options"
              >
                <i className="fas fa-list-ul"></i>
                <span>Options</span>
              </Link>
            ) : (
              <button
                type="button"
                className="btn-addon-action"
                onClick={handleAdd}
                disabled={!addon.in_stock || adding}
                title="Add to Cart"
              >
                {adding ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  <>
                    <i className="fas fa-cart-plus"></i>
                    <span>Add</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddonCard;
